import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Fullscreen image gallery with keyboard nav.
export default function Lightbox({ scenes, openIndex, onClose }) {
  const [idx, setIdx] = useState(openIndex);

  useEffect(() => setIdx(openIndex), [openIndex]);

  const prev = useCallback(() => setIdx((i) => (i - 1 + scenes.length) % scenes.length), [scenes.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % scenes.length), [scenes.length]);

  useEffect(() => {
    if (openIndex === null || openIndex === undefined) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, onClose, prev, next]);

  if (openIndex === null || openIndex === undefined) return null;
  const activeIdx = idx ?? openIndex;
  const s = scenes[activeIdx];
  if (!s) return null;
  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-[#050505]/97 backdrop-blur-xl flex items-center justify-center"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-6 right-6 text-[#EAEAEA] hover:text-[#B58B5D] transition-colors p-2"
        aria-label="Close"
      >
        <X size={28} />
      </button>
      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-4 md:left-8 text-[#EAEAEA] hover:text-[#B58B5D] p-3 transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft size={36} />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-4 md:right-8 text-[#EAEAEA] hover:text-[#B58B5D] p-3 transition-colors"
        aria-label="Next"
      >
        <ChevronRight size={36} />
      </button>
      <img
        src={s.src}
        alt={s.alt}
        className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono uppercase text-[11px] tracking-[0.2em] text-[#A3A3A3]">
        {String(activeIdx + 1).padStart(2, "0")} / {String(scenes.length).padStart(2, "0")}
      </div>
    </div>
  );
}
